import { useCallback } from 'react';
import { useAtom } from 'jotai';
import { App } from 'antd';
import {
  Customer,
  UpdateCustomerBody,
  updateCustomerResponseSchema,
} from '@minutes-system/backend/src/api/customers/schema';
import { customerQueryAtom } from '../states/state';
import { refreshAllCustomersCache } from '../../../../states/statesUtil';
import { guardedClient } from '../../../auth/api/clients';
import fetchAndValidate from '../../../../lib/fetchAndValidate';

interface UseCustomerDetailReturn {
  customer: Customer;
  updateCustomer: (data: UpdateCustomerBody) => Promise<{ success: boolean }>;
}

const useCustomerDetail = (id: number): UseCustomerDetailReturn => {
  const [customer, refreshCustomer] = useAtom(customerQueryAtom(id));
  const { message } = App.useApp();

  const updateCustomer = useCallback(
    async (data: UpdateCustomerBody): Promise<{ success: boolean }> => {
      const response = await guardedClient.customers[':id'].$put({
        param: { id: String(id) },
        json: data,
      });
      const result = await fetchAndValidate(Promise.resolve(response), updateCustomerResponseSchema);

      if (result.resultType === 'error') {
        const reasonMessages: Record<typeof result.reason, string> = {
          'exact-duplicate': '同一の顧客が既に存在します',
          'same-person-update-needed': '同一人物の可能性がある顧客が既に存在します',
          'name-changed': '姓が変更された可能性のある顧客が既に存在します',
          'mobile-duplicate': '同じ携帯電話番号の顧客が既に存在します',
        };
        message.error(reasonMessages[result.reason]);
        return { success: false };
      }

      if (result.resultType === 'success-with-warning') {
        const warningMessages: Record<typeof result.reason, string> = {
          'phone-shared': '同じ固定電話番号を使用する顧客が存在します（夫婦・家族の可能性）',
          'same-name-exists': '同姓同名の顧客が存在します',
        };
        message.warning(warningMessages[result.reason]);
      }

      refreshCustomer();
      refreshAllCustomersCache();

      return { success: true };
    },
    [id, message, refreshCustomer]
  );

  return { customer, updateCustomer };
};

export default useCustomerDetail;
